import React, { FC } from "react";
import { Modal, Box, Typography } from "@mui/material";
import { Bell } from "lucide-react";
import { Colors } from "../../../configs";

export interface INotePopupProps {
  showPopup: boolean;
  popupContent: React.ReactNode;
  onClose?: () => void;
}

const NotePopup: FC<INotePopupProps> = ({
  showPopup,
  popupContent,
  onClose,
}) => {
  const handleOnClose = () => {
    onClose && onClose();
  };

  const notes = Array.isArray(popupContent) ? popupContent : [popupContent];

  return (
    <Modal
      open={showPopup}
      onClose={handleOnClose}
      disableScrollLock
      disableRestoreFocus
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.5)" } },
      }}
    >
      <Box
        sx={{
          display: "flex",
          height: "100%",
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
          p: "1.5rem",
        }}
      >
        <Box
          sx={{
            width: "700px",
            maxWidth: "94vw",
            overflow: "hidden",
            borderRadius: "2rem",
            backgroundColor: Colors.WHITE,
            boxShadow: "0 20px 56px rgba(0,0,0,0.10)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              borderBottom: `1px solid ${Colors.ACCENT_GRAY}`,
              px: "2rem",
              py: "1.5rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: "3rem",
                height: "3rem",
                flexShrink: 0,
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "1rem",
                backgroundColor: `${Colors.SECONDARY}18`,
                color: Colors.SECONDARY,
              }}
            >
              <Bell size={22} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "var(--fs-xs)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.15em",
                  color: Colors.SECONDARY,
                  lineHeight: 1.3,
                }}
              >
                Reminder
              </Typography>
              <Typography
                sx={{
                  fontSize: "var(--fs-lg)",
                  fontWeight: 700,
                  color: Colors.TEXT_GRAY_DARK,
                  lineHeight: 1.25,
                  letterSpacing: "-0.02em",
                }}
              >
                Customer Notes
              </Typography>
            </Box>
          </Box>

          {/* Notes list */}
          <Box sx={{ px: "2rem", py: "1.5rem" }}>
            <Box
              sx={{
                maxHeight: "20rem",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {notes.map((note, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.75rem",
                    borderRadius: "1.25rem",
                    border: `1px solid ${Colors.ACCENT_GRAY}`,
                    backgroundColor: Colors.BACKGROUND_GRAY,
                    p: "1rem",
                  }}
                >
                  <Box
                    sx={{
                      mt: "0.5rem",
                      width: "0.5rem",
                      height: "0.5rem",
                      flexShrink: 0,
                      borderRadius: "50%",
                      backgroundColor: Colors.SECONDARY,
                    }}
                  />
                  <Typography
                    component="div"
                    sx={{
                      fontSize: "var(--fs-base)",
                      fontWeight: 600,
                      color: Colors.TEXT_GRAY_DARK,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      "& p": { margin: 0 },
                    }}
                  >
                    {note}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                mt: "1.5rem",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={handleOnClose}
                sx={{
                  display: "flex",
                  minWidth: "16rem",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "1rem",
                  px: "1.5rem",
                  py: "0.75rem",
                  fontSize: "var(--fs-base)",
                  fontWeight: 700,
                  border: "none",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  backgroundColor: Colors.PRIMARY,
                  color: Colors.WHITE,
                  boxShadow: "0 8px 20px rgba(1,135,82,0.28)",
                  "&:hover": {
                    boxShadow: "0 12px 28px rgba(1,135,82,0.36)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": { transform: "scale(0.98)" },
                }}
              >
                OK
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default NotePopup;
